import { movies, categoryMeta } from "@/mocks/movies";
import type { Movie } from "@/types/movie";

export type SortKey = "update" | "heat" | "rating";

export const regionOptions = ["全部", "国产", "欧美", "日韩"] as const;

export const allYearOptions = ["全部", "2024", "2023", "2022", "2021"] as const;

export const tagOptions = [
  "全部",
  "科幻",
  "悬疑",
  "古装",
  "都市",
  "爱情",
  "动作",
  "剧情",
  "奇幻",
  "温情",
] as const;

export function getCategoryMeta(key: string | undefined) {
  if (!key) return undefined;
  return categoryMeta.find((item) => item.key === key);
}

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((item) => item.id === id);
}

/** 根据分类 key 过滤出基础集合 */
export function getMoviesByCategory(key: string | undefined): Movie[] {
  if (!key) return movies;
  switch (key) {
    case "movie":
      return movies.filter((m) => m.type === "movie");
    case "tvseries":
      return movies.filter((m) => m.type === "tvseries");
    case "china":
      return movies.filter((m) => m.region === "国产");
    case "overseas":
      return movies.filter((m) => m.region === "欧美");
    case "japankorea":
      return movies.filter((m) => m.region === "日韩");
    case "latest":
      return [...movies].sort(
        (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
    case "hot":
      return [...movies].sort((a, b) => b.heat - a.heat);
    default:
      return movies;
  }
}

export interface FilterState {
  region: string;
  year: string;
  tag: string;
}

/** 组合筛选 + 排序 */
export function filterAndSort(
  list: Movie[],
  filters: FilterState,
  sort: SortKey
): Movie[] {
  const result = list.filter((m) => {
    if (filters.region !== "全部" && m.region !== filters.region) return false;
    if (filters.year !== "全部" && String(m.year) !== filters.year) return false;
    if (filters.tag !== "全部" && !m.tags.includes(filters.tag)) return false;
    return true;
  });

  switch (sort) {
    case "heat":
      return [...result].sort((a, b) => b.heat - a.heat);
    case "rating":
      return [...result].sort((a, b) => b.rating - a.rating);
    case "update":
    default:
      return [...result].sort(
        (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
  }
}

/** 相关推荐：优先同类型 + 相同标签，其次同地区 */
export function getRelatedMovies(current: Movie, limit = 6): Movie[] {
  const scored = movies
    .filter((m) => m.id !== current.id)
    .map((m) => {
      let score = 0;
      if (m.type === current.type) score += 3;
      if (m.region === current.region) score += 1;
      score += m.tags.filter((t) => current.tags.includes(t)).length * 2;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score || b.m.heat - a.m.heat);
  return scored.slice(0, limit).map((item) => item.m);
}

/** 关键词模糊搜索：名称、导演、主演、标签 */
export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) => {
    const haystack = [m.title, m.director, ...m.actors, ...m.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function formatHeat(heat: number): string {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)}万`;
  return String(heat);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
    .slice(0, limit);
}

export function getHotMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}